/**
 * Video Background Hero
 * 
 * Hero with video playing in the background.
 * Falls back to image if no video is available.
 */

import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { RichTextDisplay } from "@/components/ui/rich-text-display";

const HeroVideo = () => {
  const { features, data } = useSiteSettings();
  const { currentLayout } = useLayout();
  const config = useSiteConfig();
  const { hero } = config;
  const heroConfig = currentLayout.hero;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  
  const heroImageEnabled = (data?.features as Record<string, unknown>)?.heroImage !== false;
  
  // Placeholder video URL - in production this would come from settings
  const videoUrl = (data?.features as Record<string, { videoUrl?: string }>)?.hero?.videoUrl;

  const heightClasses = {
    full: 'min-h-screen',
    large: 'min-h-[80vh]',
    medium: 'min-h-[60vh]',
    small: 'min-h-[40vh]',
  };

  const alignmentClasses = {
    left: 'text-left lg:text-left items-start',
    center: 'text-center items-center',
    right: 'text-right lg:text-right items-end',
  };

  const handleToggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className={cn(
      "relative flex items-center justify-center overflow-hidden",
      heightClasses[heroConfig.height]
    )}>
      {/* Video/Image Background */}
      <div className="absolute inset-0">
        {videoUrl ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        ) : heroImageEnabled ? (
          <div className="w-full h-full bg-primary" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary via-primary/90 to-heritage/30" />
        )}
        
        {/* Overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-primary/50"
          style={{ opacity: heroConfig.overlayOpacity / 100 }}
        />
      </div>

      {/* Mute/Unmute Button */}
      {videoUrl && (
        <button
          onClick={handleToggleMute}
          className="absolute bottom-8 right-8 z-20 p-3 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-colors"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <VolumeX className="w-6 h-6 text-white" />
          ) : (
            <Volume2 className="w-6 h-6 text-white" />
          )}
        </button>
      )}

      {/* Content */}
      <div className={cn(
        "relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col",
        alignmentClasses[heroConfig.contentAlignment]
      )}>
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {hero.title}
            <span className="block bg-gradient-to-r from-accent to-heritage bg-clip-text text-transparent">
              {hero.highlightedText}
            </span>
          </h1>
          
          <div className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
            <RichTextDisplay content={hero.subtitle || ''} prose={false} />
          </div>

          {/* CTA Buttons */}
          {heroConfig.showCTA && (
            <div className={cn(
              "flex flex-col sm:flex-row gap-4",
              heroConfig.contentAlignment === 'center' && "justify-center",
              heroConfig.contentAlignment === 'right' && "justify-end"
            )}>
              {features.membership.enabled && (
                <Button variant="heritage" size="lg" asChild className="group">
                  <Link to="/membership">
                    <Users className="w-5 h-5" />
                    Join Our Community
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
              
              {features.donation.enabled && (
                <Button variant="donate" size="lg" asChild className="group">
                  <Link to="/donate">
                    <Heart className="w-5 h-5" />
                    Support Us
                  </Link>
                </Button>
              )}
              
              {features.events.enabled && (
                <Button variant="culture" size="lg" asChild className="group">
                  <Link to="/events">
                    <Calendar className="w-5 h-5" />
                    View Events
                  </Link>
                </Button>
              )}

              {!features.membership.enabled && !features.donation.enabled && !features.events.enabled && (
                <Button variant="heritage" size="lg" asChild className="group">
                  <Link to="/about-us">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default HeroVideo;



